import styled from 'styled-components';
import { useState } from 'react';

import { LiveEvent } from '@/pages/LiveEvent/store/liveEvent/types';
import { Button } from '@/ui/Button';
import { LiveEventHighlightsActiveSessionReservation } from '@/pages/LiveEvent/LiveEventActiveSessionReservation/LiveEventActiveSessionReservation';
import { LiveEventSessionPicker } from '@/pages/LiveEvent/LiveEventSessionPicker/LiveEventSessionPicker';
import { LiveEventHighlightsInformerBanner } from '@/pages/LiveEvent/LiveEventHighlightsInformerBanner/LiveEventHighlightsInformerBanner';
import { useCurrentUserLiveEventJourneyParticipant } from '@/pages/LiveEvent/store/useCurrentUserLiveEventJourneyParticipant';
import { useLiveEventLocations } from '@/pages/LiveEvent/LiveEventLocationsContext';
import { useLiveEventAddToCalendar } from '@/pages/LiveEvent/store/useLiveEventAddToCalendar';
import { MenuList } from '@/ui/MenuList';
import { Skeleton } from '@/ui/Skeleton';

type LiveEventHighlightsBodyProps = Pick<LiveEvent, 'title' | 'id' | 'teaser'> & {
  parentJourneyId?: number;
};

const ReserveButtonStyled = styled(Button)`
  width: 100%;
`;

const LoadingStateStyled = styled(Skeleton)`
  width: 100%;
`;

export const LiveEventHighlightsBody = ({ title, parentJourneyId, id, teaser }: LiveEventHighlightsBodyProps): JSX.Element => {
  const { locations, activeLocation, getLocationNameById, changeEnrollmentToLocation, isChangingEnrollment } =
    useLiveEventLocations();
  const { isJourneyParticipant, loading: isJourneyParticipantLoading } = useCurrentUserLiveEventJourneyParticipant({
    journeyId: parentJourneyId,
  });

  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);

  const isSingleSession = locations?.length === 1;
  const activeSessionReservationTitle = isSingleSession
    ? 'You have a seat for this event'
    : `You have a seat for ${getLocationNameById(activeLocation?.id)}`;

  const addToCalendarProps = useLiveEventAddToCalendar({
    id: id || '',
    room: activeLocation?.room,
    title,
    teaser,
    address: activeLocation?.address,
    endDate: activeLocation?.endDate,
    startDate: activeLocation?.startDate,
    myEnrollment: activeLocation?.myEnrollment,
  });

  const isSelectedSessionDifferentFromActiveSession = Boolean(selectedSessionId && selectedSessionId !== activeLocation?.id);

  if (isJourneyParticipantLoading) {
    return <LoadingStateStyled height={100} />;
  }

  if (!isJourneyParticipant) {
    return (
      <LiveEventHighlightsInformerBanner>
        You need to be part of the journey that includes this event to have a seat.
      </LiveEventHighlightsInformerBanner>
    );
  }

  return (
    <>
      <LiveEventHighlightsActiveSessionReservation
        title={activeSessionReservationTitle}
        date={activeLocation?.dateWithoutWeekDay || ''}
        type={activeLocation?.type}
      />
      {!isSingleSession && (
        <>
          <LiveEventSessionPicker
            selectedSessionId={selectedSessionId}
            activeSessionId={activeLocation?.id}
            setSelectedSessionId={setSelectedSessionId}
          />
          {isSelectedSessionDifferentFromActiveSession && (
            <>
              <LiveEventHighlightsInformerBanner>
                <div>
                  You currently have a seat for <strong>{getLocationNameById(activeLocation?.id)}</strong>. Click below to move
                  your reservation <br /> to <strong>{getLocationNameById(selectedSessionId)}</strong>.
                </div>
              </LiveEventHighlightsInformerBanner>
              <ReserveButtonStyled
                variant="primary"
                onClick={() => {
                  if (!selectedSessionId) return;
                  changeEnrollmentToLocation({ newLocationId: selectedSessionId });
                }}
                disabled={isChangingEnrollment}
              >
                Reserve new seat
              </ReserveButtonStyled>
            </>
          )}
        </>
      )}

      <MenuList
        {...addToCalendarProps}
        triggerButtonProps={{ variant: 'secondary', icon: ['far', 'calendar'], style: { width: '100%' } }}
      />
    </>
  );
};
